import * as React from 'react';
import { type Subscription, useSubscription } from 'use-subscription';

import { HistoryContext, LocationContext } from './contexts';
import { createHistory, type Location, type NavigationHistory } from './createHistory';

/**
 * Provide the history controller and the current location to the react tree.
 * See: `useHistory` and `useHistoryLocation` to access the history and location.
 */
export function HistoryProvider(props: {
    history?: NavigationHistory;
    children: React.ReactNode;
}): React.ReactElement {
    const { history: propHistory, children } = props;

    const history = React.useMemo(() => {
        return propHistory || createHistory();
    }, [propHistory]);

    const subscription = React.useMemo<Subscription<Location>>(() => {
        return {
            getCurrentValue: () => history.location,
            subscribe: (callback) => {
                return history.listen(callback);
            },
        };
    }, [history]);

    const location = useSubscription(subscription);

    return (
        <HistoryContext.Provider value={history}>
            <LocationContext.Provider value={location}>{children}</LocationContext.Provider>
        </HistoryContext.Provider>
    );
}
